// TriageOs

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { getAssets, getAsset, createAsset, updateAsset, deleteAsset, updateAssetStrategy, suggestStrategy, analyzeAsset } from './services/api';
import Header from './components/common/Header';
import AssetModal from './components/AssetManager/AssetModal';
import AssetManagerContent from './components/AssetManager/AssetManagerContent';
import AssetTriagePage from './components/Triage/AssetTriagePage';
import CircularEconomyDashboardContent from './components/Dashboard/CircularEconomyDashboardContent';
import LandingPage from './components/LandingPage';
import AboutPage from './components/AboutPage';
import LoadingSpinner from './components/LoadingSpinner';
import Footer from './components/common/Footer';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
// --- CORE APPLICATION CONSTANTS ---

const MOCK_USER_ID = "circular_analyst@example.com";
//const LOCAL_STORAGE_KEY = "circularAssetsV6";

// --- CUSTOM HOOKS ---

const useAssets = () => {
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load assets on component mount

  const addAsset = useCallback(async (assetData) => {
    try {
      const createdAsset = await createAsset(assetData);
      setAssets(prev => [...prev, createdAsset]);
      return { success: true, data: createdAsset };
    } catch (error) {
      console.error('Failed to create asset:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create asset. Please try again.'
      };
    }
  }, []);

  const updateAssetData = useCallback(async (id, assetData) => {
    try {
      await updateAsset(id, assetData);

      // The API returns { message: "Asset updated" }, not the updated asset data
      // We need to fetch the updated asset data
      const freshAsset = await getAsset(id);

      setAssets(prev => prev.map(asset => asset.id === id ? freshAsset : asset));
      return { success: true, data: freshAsset };
    } catch (error) {
      console.error('Failed to update asset:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update asset. Please try again.'
      };
    }
  }, [getAsset]);

  // Trigger AI analysis after asset update to refresh AI fields
  const triggerAIAnalysis = useCallback(async (assetId) => {
    try {
      // Trigger backend AI to recompute fields
      await analyzeAsset(assetId);
      await suggestStrategy(assetId);

      // Poll backend briefly until AI fields are populated (handles async processing)
      const deadline = Date.now() + 8000; // up to 8s (to allow both AI fields to arrive)
      let updatedAsset = null;
      // Immediately fetch once
      updatedAsset = await getAsset(assetId);
      const hasBothAiFields = (a) => a && (a.circularity_score != null && a.ai_recommended_r_strategy);
      while (!hasBothAiFields(updatedAsset) && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 500));
        updatedAsset = await getAsset(assetId);
      }

      // Update list state regardless of whether AI fields appeared (last fetch wins)
      setAssets(prev => prev.map(asset => asset.id === assetId ? updatedAsset : asset));
      return updatedAsset;
    } catch (error) {
      console.error('Failed to trigger AI analysis:', error);
      return null;
    }
  }, [getAsset]);

  const removeAsset = useCallback(async (id) => {
    try {
      await deleteAsset(id);
      setAssets(prev => prev.filter(asset => asset.id !== id));
      return { success: true };
    } catch (error) {
      console.error('Failed to delete asset:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to delete asset. Please try again.' 
      };
    }
  }, []);

  const updateStrategy = useCallback(async (assetId, strategyKey) => {
    try {
      await updateAssetStrategy(assetId, strategyKey);
      setAssets(prev => prev.map(asset =>
        asset.id === assetId ? { ...asset, current_strategy: strategyKey } : asset
      ));
      return { success: true };
    } catch (error) {
      console.error('Failed to update strategy:', error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to update strategy. Please try again.' 
      };
    }
  }, []);

  useEffect(() => {
    const loadAssets = async () => {
      try {
        setIsLoading(true);
        console.log('Loading assets on component mount using getAssets()...');
        const data = await getAssets();
        console.log('Assets loaded successfully:', Array.isArray(data) ? `${data.length} assets` : 'non-array data');
        console.log('Raw API response:', data);

        // Handle Django REST Framework pagination format
        let assetsArray = [];
        if (Array.isArray(data)) {
          assetsArray = data;
        } else if (data && data.results && Array.isArray(data.results)) {
          assetsArray = data.results;
          console.log(`Extracted ${assetsArray.length} assets from paginated response`);
        } else {
          console.warn('Unexpected API response format:', data);
          assetsArray = [];
        }

        setAssets(assetsArray);
        setError(null);
      } catch (err) {
        console.error('Failed to load assets:', err);
        setError('Failed to load assets. Please try again later.');
        setAssets([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadAssets();
  }, []);

  const getAssetById = useCallback(async (id) => {
    try {
      console.log(`Fetching individual asset with ID ${id} using getAsset()...`);
      const asset = await getAsset(id);
      console.log('Individual asset fetched successfully:', asset);
      return { success: true, data: asset };
    } catch (error) {
      console.error('Failed to fetch asset:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch asset. Please try again.'
      };
    }
  }, []);

  const reloadAssets = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Manually reloading assets using getAssets()...');
      const data = await getAssets();
      console.log('Assets reloaded successfully:', Array.isArray(data) ? `${data.length} assets` : 'non-array data');

      // Handle Django REST Framework pagination format
      let assetsArray = [];
      if (Array.isArray(data)) {
        assetsArray = data;
      } else if (data && data.results && Array.isArray(data.results)) {
        assetsArray = data.results;
        console.log(`Extracted ${assetsArray.length} assets from paginated response`);
      } else {
        console.warn('Unexpected API response format:', data);
        assetsArray = [];
      }

      setAssets(assetsArray);
      setError(null);
    } catch (err) {
      console.error('Failed to reload assets:', err);
      setError('Failed to reload assets. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    assets,
    setAssets,
    isLoading,
    error,
    getAsset: getAssetById,
    addAsset,
    updateAsset: updateAssetData,
    removeAsset,
    updateStrategy,
    reloadAssets,
    triggerAIAnalysis
  };
};

// --- MAIN APP COMPONENT ---

function App() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('landing');
  const [dashboardSelectedAssetId, setDashboardSelectedAssetId] = useState(null);
  const [triageAsset, setTriageAsset] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ sortBy: null, sortDirection: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingTriage, setIsLoadingTriage] = useState(false);

  // Initialize assets hook BEFORE effects that reference its values (e.g., reloadAssets) to avoid TDZ in dependency arrays
  const {
    assets,
    setAssets,
    isLoading,
    error,
    getAsset,
    addAsset,
    updateAsset,
    removeAsset,
    updateStrategy,
    reloadAssets,
    triggerAIAnalysis
  } = useAssets();

  // Post-login redirect to last intended view
  useEffect(() => {
    if (isAuthenticated) {
      try {
        const v = localStorage.getItem('triageos_post_login_view');
        if (v) {
          setView(v);
          localStorage.removeItem('triageos_post_login_view');
        }
      } catch (e) {}
    }
  }, [isAuthenticated]);

  // After login, ensure protected data is fetched so login-only pages show real data immediately
  useEffect(() => {
    if (isAuthenticated) {
      try {
        if (typeof reloadAssets === 'function') {
          reloadAssets();
        }
      } catch (e) {}
    }
  }, [isAuthenticated, reloadAssets]);

  const showMessage = useCallback((message) => {
    console.log(message);
    // TODO: Replace with proper toast notification system
  }, []);

  const handleOpenAddModal = useCallback(() => {
    setEditAsset(null);
    setShowModal(true);
  }, []);

  const handleOpenEditModal = useCallback((asset) => {
    setEditAsset(asset);
    setShowModal(true);
  }, []);

  const handleDeleteAsset = useCallback(async (id) => {
    const result = await removeAsset(id);
    if (!result.success) {
      showMessage(result.error);
    }
  }, [removeAsset, showMessage]);

  const handleAssetClick = useCallback(async (id) => {
    try {
      setIsLoadingTriage(true);
      const { success, data, error } = await getAsset(id);
      if (success) {
        setTriageAsset(data);
        setView('triage');
      } else {
        showMessage(error || `Failed to load asset ${id}`);
      }
    } catch (error) {
      console.error('Error loading asset:', error);
      showMessage('An error occurred while loading the asset');
    } finally {
      setIsLoadingTriage(false);
    }
  }, [getAsset, showMessage]);

  const handleSaveAsset = useCallback(async (data, isEditing) => {
    let result;
    if (isEditing) {
      result = await updateAsset(data.id, data);
    } else {
      result = await addAsset(data);
    }

    if (result.success) {
      showMessage(isEditing ? 'Asset updated successfully!' : 'Asset created successfully!');

      // If the triage view is currently open for this asset, update it immediately with the edited values
      if (isEditing && triageAsset && triageAsset.id === data.id) {
        // result.data is the freshly fetched edited asset from updateAsset()
        setTriageAsset(result.data);
      }

      // Trigger AI analysis to update circularity score and AI recommendations
      if (isEditing) {
        const updatedAfterAI = await triggerAIAnalysis(data.id);
        // Keep Triage page in sync with latest AI fields when editing the same asset
        if (triageAsset && triageAsset.id === data.id && updatedAfterAI) {
          setTriageAsset(updatedAfterAI);
        }
      }
    } else {
      showMessage(result.error);
    }

    setShowModal(false);
    setEditAsset(null);
  }, [addAsset, updateAsset, triggerAIAnalysis, showMessage, triageAsset]);

  const handleSaveStrategy = useCallback(async (assetId, strategyKey) => {
    const result = await updateStrategy(assetId, strategyKey);
    if (result.success) {
      showMessage(`Strategy ${strategyKey} saved for asset ${assetId}`);
    } else {
      showMessage(result.error);
    }
  }, [updateStrategy, showMessage]);

  const handleSaveAIRecommendations = useCallback(async (assetId) => {
    try {
      await suggestStrategy(assetId);
      await reloadAssets();
      showMessage(`AI recommendations saved for asset ${assetId}`);
    } catch (error) {
      console.error('Failed to save AI recommendations:', error);
      showMessage('Failed to save AI recommendations. Please try again.');
    }
  }, [reloadAssets, showMessage]);

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  const handleSortChange = useCallback((key) => {
    setSortConfig(prev => ({
      sortBy: key,
      sortDirection: prev.sortBy === key && prev.sortDirection === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  // Memoized configurations to prevent unnecessary re-renders
  const searchConfig = useMemo(() => ({ query: searchQuery }), [searchQuery]);
  const sortConfigMemo = useMemo(() => sortConfig, [sortConfig.sortBy, sortConfig.sortDirection]);
  const pageConfig = useMemo(() => ({ currentPage }), [currentPage]);

  // Render app shell and pages regardless of data loading errors so static pages (e.g., Landing, About) always load
  return (
    <div className="min-h-screen bg-gray-100">
      <Header userId={MOCK_USER_ID} currentView={view} setView={setView} />

      {view === 'landing' && (
        <LandingPage onNavigate={setView} />
      )}

      {view === 'about' && (
        <AboutPage onNavigate={setView} />
      )}

      {view === 'dashboard' && (
        isAuthenticated ? (
          <CircularEconomyDashboardContent
            assets={assets}
            dashboardSelectedAssetId={dashboardSelectedAssetId}
            setDashboardSelectedAssetId={setDashboardSelectedAssetId}
            onReloadAssets={reloadAssets}
            isLoadingAssets={isLoading}
            error={error}
          />
        ) : (
          <div className="container mx-auto px-4 py-8">
            <div className="p-4 rounded border border-yellow-200 bg-yellow-50 text-yellow-800 mb-4">Please sign in to view your dashboard.</div>
            <button
              onClick={() => { try { localStorage.setItem('triageos_post_login_view', 'dashboard'); } catch(e) {} navigate('/login', { state: { fromView: 'dashboard' } }); }}
              className="px-3 py-2 rounded bg-cyan-600 text-white hover:bg-cyan-700 text-sm"
            >Sign in</button>
          </div>
        )
      )}

      {view === 'management' && (
        isAuthenticated ? (
          <AssetManagerContent
            assets={assets}
            searchConfig={searchConfig}
            onSearchChange={handleSearchChange}
            sortConfig={sortConfigMemo}
            onSortChange={handleSortChange}
            pageConfig={pageConfig}
            onPageChange={handlePageChange}
            onOpenAddModal={handleOpenAddModal}
            onOpenEditModal={handleOpenEditModal}
            onOpenDeleteConfirm={handleDeleteAsset}
            onAssetClick={handleAssetClick}
            onReloadAssets={reloadAssets}
            isLoading={isLoading}
            error={error}
          />
        ) : (
          <div className="container mx-auto px-4 py-8">
            <div className="p-4 rounded border border-yellow-200 bg-yellow-50 text-yellow-800 mb-4">Please sign in to manage assets.</div>
            <button
              onClick={() => { try { localStorage.setItem('triageos_post_login_view', 'management'); } catch(e) {} navigate('/login', { state: { fromView: 'management' } }); }}
              className="px-3 py-2 rounded bg-cyan-600 text-white hover:bg-cyan-700 text-sm"
            >Sign in</button>
          </div>
        )
      )}

      {view === 'triage' && triageAsset && (
        <AssetTriagePage
          asset={triageAsset}
          onBack={() => {
            setTriageAsset(null);
            setView('management');
          }}
          onSaveStrategy={handleSaveStrategy}
          onSaveAIRecommendations={handleSaveAIRecommendations}
          showMessage={showMessage}
          isLoading={isLoadingTriage}
        />
      )}

      {showModal && (
        <AssetModal
          asset={editAsset}
          onClose={() => { setShowModal(false); setEditAsset(null); }}
          onSave={handleSaveAsset}
        />
      )}

      <Footer onNavigate={setView} />
    </div>
  );
}

export default App;
